import React from "react";
import { Link } from "react-router-dom";
import { AuthImg } from "@/components/AuthImg";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, CalendarDays, FileText, Image as ImageIcon, MessageSquareQuote, ScrollText, Lock, Pencil, Archive, UserRound, Crown, CalendarRange, AlertTriangle } from "lucide-react";
import { VerificationBadge } from "@/components/VerificationBadge";

export const RELIABILITY = {
  documented: { label: "Fait documenté", color: "#2C4230", bg: "#E6EFE7" },
  oral_tradition: { label: "Tradition orale", color: "#3E6B8A", bg: "#E1ECF3" },
  hypothesis: { label: "Hypothèse à vérifier", color: "#A96B18", bg: "#FBEDD6" },
};
export const KINDS = {
  text: { label: "Récit / texte", icon: ScrollText },
  event: { label: "Événement daté", icon: CalendarDays },
  testimony: { label: "Témoignage", icon: MessageSquareQuote },
  document: { label: "Document", icon: FileText },
  photo: { label: "Photographie", icon: ImageIcon },
  reference: { label: "Référence bibliographique", icon: BookOpen },
  witness: { label: "Fiche de témoin", icon: UserRound },
  chief: { label: "Fiche de chef", icon: Crown },
  period: { label: "Période historique", icon: CalendarRange },
};

export const ReliabilityBadge = ({ value }) => {
  const r = RELIABILITY[value] || RELIABILITY.hypothesis;
  return (
    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border"
      style={{ color: r.color, background: r.bg, borderColor: r.color }} data-testid={`reliability-${value}`}>{r.label}</span>
  );
};

const FileLink = ({ path }) => {
  const { API, token } = useAuth();
  const open = async () => {
    const res = await fetch(`${API}/files/${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
    if (res.ok) window.open(URL.createObjectURL(await res.blob()), "_blank");
  };
  return <button type="button" onClick={open} className="text-sm text-[#993D22] underline" data-testid="entry-file-link">Ouvrir le document</button>;
};

export const dateText = (e) => e.date_label || (e.year != null ? `${e.year_is_estimate ? "vers " : ""}${e.year}${e.end_year ? `–${e.end_year}` : ""}` : "");

export const HistoryEntry = ({ e, canEdit, onEdit, onArchive, sectionTitle, sectionsBy }) => {
  const K = KINDS[e.kind] || KINDS.text;
  const Icon = K.icon;
  return (
    <article className="heritage-card p-5" data-testid={`history-entry-${e.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1 text-xs text-[#6E5B4F]"><Icon className="w-3.5 h-3.5" />{K.label}</span>
        <ReliabilityBadge value={e.reliability} />
        <VerificationBadge status={e.validation_status || "to_verify"} />
        {dateText(e) && <span className="text-xs text-[#3A2312] px-2 py-0.5 rounded-full bg-[#F0E6D8]">{dateText(e)}</span>}
        {e.visibility === "members" && <span className="inline-flex items-center gap-1 text-[10px] text-[#6E5B4F]"><Lock className="w-3 h-3" />Membres</span>}
        {sectionTitle && <span className="text-[10px] uppercase tracking-wider text-[#993D22]">{sectionTitle}</span>}
      </div>
      <h3 className="font-serif-heritage text-2xl text-[#261911] mb-2">{e.title}</h3>
      {e.kind === "photo" && e.file_path && <AuthImg path={e.file_path} alt={e.title} className="rounded-lg max-h-96 mb-3" />}
      {e.body && <p className="text-[#3A2312] whitespace-pre-wrap leading-relaxed">{e.body}</p>}
      {e.witness_name && <p className="text-sm text-[#6E5B4F] mt-2">Témoin : <span className="font-semibold">{e.witness_name}</span></p>}
      {e.divergences && (
        <div className="mt-3 p-3 rounded-lg bg-[#FBEDD6] text-sm text-[#3A2312]" data-testid="entry-divergences">
          <div className="flex items-center gap-1 font-semibold text-[#A96B18] mb-1"><AlertTriangle className="w-4 h-4" />Divergences entre les sources</div>
          <p className="whitespace-pre-wrap">{e.divergences}</p>
        </div>
      )}
      {e.reference && <p className="text-sm italic text-[#6E5B4F] mt-2 border-l-2 border-[#D9822B] pl-3" data-testid="entry-reference">Source : {e.reference}{e.pages ? ` — p. ${e.pages}` : ""}</p>}
      {(e.related_section_keys || []).length > 0 && sectionsBy && (
        <p className="text-xs mt-2 text-[#6E5B4F]">Voir aussi : {e.related_section_keys.map(k => sectionsBy[k] && <Link key={k} to={`/histoire?rubrique=${k}`} className="text-[#993D22] underline mr-2">{sectionsBy[k].title}</Link>)}</p>
      )}
      {e.kind !== "photo" && e.file_path && <div className="mt-2"><FileLink path={e.file_path} /></div>}
      <div className="flex items-center justify-between mt-3 text-[11px] text-[#6E5B4F]">
        <span>Ajouté par {e.created_by_name || "—"}{e.updated_by_name ? ` · modifié par ${e.updated_by_name}` : ""}</span>
        {canEdit && (
          <span className="flex gap-2">
            <button onClick={() => onEdit(e)} className="inline-flex items-center gap-1 text-[#993D22]" data-testid={`edit-entry-${e.id}`}><Pencil className="w-3 h-3" />Modifier</button>
            <button onClick={() => onArchive(e)} className="inline-flex items-center gap-1 text-[#6E5B4F]" data-testid={`archive-entry-${e.id}`}><Archive className="w-3 h-3" />Archiver</button>
          </span>
        )}
      </div>
    </article>
  );
};

export const SectionLinks = ({ sectionKey, clanKey }) => {
  if (sectionKey === "chefferie") return <Link to="/chefs" className="text-sm text-[#993D22] underline">Voir la liste des chefs d'Ekom 1 →</Link>;
  if (clanKey) return <Link to={`/clans/${clanKey}`} className="text-sm text-[#993D22] underline">Voir les membres et la généalogie de cette famille →</Link>;
  return null;
};

export default HistoryEntry;
